import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card, Typography, Descriptions, Tag, Spin, Button, Space, Popconfirm,
  Modal, Form, Input, InputNumber, Select, message, Timeline, Drawer,
  List, Badge, Tooltip, Divider, Empty,
} from 'antd';
import { ArrowLeft, Pencil, Trash2, User, Home, Zap, ExternalLink, Link2, CalendarPlus, X } from 'lucide-react';
import { opportunitiesApi, leadsApi, propertiesApi } from '../../services/api';

const { Title, Text } = Typography;

const stageLabels: Record<string, string> = {
  INITIAL_CONTACT: 'Contacto inicial', VISIT_SCHEDULED: 'Visita programada',
  VISIT_DONE: 'Visita realizada', OFFER_MADE: 'Oferta enviada',
  NEGOTIATION: 'Negociación', DOCUMENTATION: 'Documentación',
  CLOSED_WON: 'Cerrada ganada', CLOSED_LOST: 'Cerrada perdida',
};

const stageColors: Record<string, string> = {
  INITIAL_CONTACT: 'blue', VISIT_SCHEDULED: 'cyan',
  VISIT_DONE: 'green', OFFER_MADE: 'gold',
  NEGOTIATION: 'orange', DOCUMENTATION: 'purple',
  CLOSED_WON: 'success', CLOSED_LOST: 'error',
};

const typeLabels: Record<string, string> = {
  VILLA: 'Villa', APARTMENT: 'Apartamento', FINCA: 'Finca',
  PENTHOUSE: 'Ático', TOWNHOUSE: 'Adosado', COMMERCIAL: 'Comercial',
  LAND: 'Solar', GARAGE: 'Garaje', OFFICE: 'Oficina', STORAGE: 'Trastero',
  BUILDING: 'Edificio', ROOM: 'Habitación', OTHER: 'Otro',
};

function ScoreBadge({ score }: { score: number }) {
  const color = score >= 70 ? '#52c41a' : score >= 40 ? '#fa8c16' : '#8c8c8c';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: 40, height: 40, borderRadius: '50%', border: `2px solid ${color}`,
      color, fontWeight: 700, fontSize: 13, flexShrink: 0,
    }}>
      {score}
    </span>
  );
}

function MatchPropertyCard({
  item, onLink, onDiscard,
}: {
  item: any;
  onLink: (id: string) => void;
  onDiscard: (id: string) => void;
}) {
  const image = Array.isArray(item.images) && item.images.length > 0 ? item.images[0]?.url : null;
  return (
    <div style={{ display: 'flex', gap: 12, padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
      {image ? (
        <img src={image} alt={item.title} style={{ width: 72, height: 56, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }} />
      ) : (
        <div style={{ width: 72, height: 56, borderRadius: 6, background: '#f5f5f5', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Home size={20} color="#bbb" />
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 4 }}>
          <Text strong style={{ fontSize: 13, flex: 1 }}>{item.title}</Text>
          <ScoreBadge score={item.score} />
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
          {item.type && <Tag style={{ margin: 0 }}>{typeLabels[item.type] || item.type}</Tag>}
          {item.zone && <Tag style={{ margin: 0 }} color="blue">{item.zone}</Tag>}
          {item.bedrooms != null && <Tag style={{ margin: 0 }}>🛏 {item.bedrooms}</Tag>}
          {item.size && <Tag style={{ margin: 0 }}>{item.size} m²</Tag>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <Text strong style={{ color: '#1677ff', fontSize: 14 }}>
            €{item.price?.toLocaleString('es-ES')}
          </Text>
          <Space size={4}>
            <Tooltip title="Vincular a esta oportunidad">
              <Button size="small" type="primary" icon={<Link2 size={12} />} onClick={() => onLink(item.id)}>
                Vincular
              </Button>
            </Tooltip>
            <Tooltip title="Descartar resultado">
              <Button size="small" icon={<X size={12} />} onClick={() => onDiscard(item.id)} />
            </Tooltip>
          </Space>
        </div>
      </div>
    </div>
  );
}

function MatchWitnessCard({ item, onDiscard }: { item: any; onDiscard: (id: string) => void }) {
  const zone = item.zone ? (item.zone.neighborhood || item.zone.zone || item.zone.municipality) : '—';
  return (
    <div style={{ display: 'flex', gap: 12, padding: '10px 0', borderBottom: '1px solid #f0f0f0' }}>
      <div style={{ width: 72, height: 50, borderRadius: 6, background: '#fafafa', border: '1px solid #e8e8e8', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: '#999', textTransform: 'uppercase', fontWeight: 600, letterSpacing: 1 }}>
        {item.source}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 4 }}>
          <Text style={{ fontSize: 12, flex: 1, color: '#555' }}>{item.title || 'Sin título'}</Text>
          <ScoreBadge score={item.score} />
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
          {zone !== '—' && <Tag style={{ margin: 0 }} color="blue">{zone}</Tag>}
          {item.size && <Tag style={{ margin: 0 }}>{item.size} m²</Tag>}
          {item.pricePerM2 && <Tag style={{ margin: 0 }}>{Math.round(item.pricePerM2).toLocaleString('es-ES')} €/m²</Tag>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Text strong style={{ color: '#1677ff', fontSize: 14 }}>
            €{item.price?.toLocaleString('es-ES')}
          </Text>
          <Space size={4}>
            {item.url && (
              <Tooltip title="Ver en portal">
                <Button size="small" icon={<ExternalLink size={12} />} href={item.url} target="_blank" />
              </Tooltip>
            )}
            <Tooltip title="Descartar resultado">
              <Button size="small" icon={<X size={12} />} onClick={() => onDiscard(item.id)} />
            </Tooltip>
          </Space>
        </div>
      </div>
    </div>
  );
}

export default function OpportunityDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [opp, setOpp] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [leads, setLeads] = useState<any[]>([]);
  const [properties, setProperties] = useState<any[]>([]);
  const [form] = Form.useForm();

  // Match state
  const [matchOpen, setMatchOpen] = useState(false);
  const [matchLoading, setMatchLoading] = useState(false);
  const [matchData, setMatchData] = useState<any>(null);
  const [discarded, setDiscarded] = useState<Set<string>>(new Set());

  const fetchOpp = useCallback(() => {
    if (!id) return;
    setLoading(true);
    opportunitiesApi.get(id)
      .then((r) => setOpp(r.data))
      .catch(() => message.error('No se encontró'))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { fetchOpp(); }, [fetchOpp]);

  const openEdit = async () => {
    const [l, p] = await Promise.all([
      leadsApi.list({ limit: 100 }),
      propertiesApi.list({ limit: 100 }),
    ]);
    setLeads(l.data.data);
    setProperties(p.data.data);
    form.setFieldsValue({
      title: opp.title,
      leadId: opp.leadId || opp.lead?.id,
      propertyId: opp.propertyId || opp.property?.id,
      value: opp.value,
      probability: opp.probability,
      stage: opp.stage,
      notes: opp.notes,
    });
    setModalOpen(true);
  };

  const handleSubmit = async (values: any) => {
    try {
      await opportunitiesApi.update(id!, values);
      message.success('Oportunidad actualizada');
      setModalOpen(false);
      fetchOpp();
    } catch { message.error('Error al actualizar'); }
  };

  const handleDelete = async () => {
    try {
      await opportunitiesApi.delete(id!);
      message.success('Oportunidad eliminada');
      navigate('/opportunities');
    } catch { message.error('Error al eliminar'); }
  };

  const handleStageChange = async (stage: string) => {
    try {
      await opportunitiesApi.update(id!, { stage });
      message.success(`Etapa → ${stageLabels[stage]}`);
      fetchOpp();
    } catch { message.error('Error al cambiar etapa'); }
  };

  const openMatch = async () => {
    setMatchOpen(true);
    setMatchData(null);
    setDiscarded(new Set());
    setMatchLoading(true);
    try {
      const r = await opportunitiesApi.match(id!);
      setMatchData(r.data);
    } catch {
      message.error('Error al buscar matches');
    } finally {
      setMatchLoading(false);
    }
  };

  const handleLinkProperty = async (propertyId: string) => {
    try {
      await opportunitiesApi.update(id!, { propertyId });
      message.success('Propiedad vinculada a la oportunidad');
      setMatchOpen(false);
      fetchOpp();
    } catch { message.error('Error al vincular propiedad'); }
  };

  const handleDiscard = (itemId: string) => {
    setDiscarded((prev) => new Set([...prev, itemId]));
  };

  const visibleProperties = matchData?.properties?.filter((p: any) => !discarded.has(p.id)) ?? [];
  const visibleWitnesses = matchData?.witnesses?.filter((w: any) => !discarded.has(w.id)) ?? [];
  const prefs = matchData?.leadPreferences;

  if (loading) return <div style={{ textAlign: 'center', padding: 100 }}><Spin size="large" /></div>;
  if (!opp) return <Title level={4}>Oportunidad no encontrada</Title>;

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Space>
          <Button icon={<ArrowLeft size={14} />} onClick={() => navigate('/opportunities')}>Volver</Button>
          <Title level={3} style={{ margin: 0 }}>{opp.title}</Title>
          <Tag color={stageColors[opp.stage]}>{stageLabels[opp.stage]}</Tag>
        </Space>
        <Space>
          <Button icon={<Zap size={14} />} type="primary" onClick={openMatch}>Hacer Match</Button>
          <Button icon={<Pencil size={14} />} onClick={openEdit}>Editar</Button>
          <Popconfirm title="¿Eliminar esta oportunidad?" onConfirm={handleDelete} okText="Sí" cancelText="No">
            <Button danger icon={<Trash2 size={14} />}>Eliminar</Button>
          </Popconfirm>
        </Space>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16 }}>
        {/* Main info */}
        <div>
          <Card title="Información General" style={{ marginBottom: 16 }}>
            <Descriptions column={2} bordered size="small">
              <Descriptions.Item label="Valor">
                {opp.value ? `€${opp.value.toLocaleString()}` : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Probabilidad">
                {opp.probability != null ? `${opp.probability}%` : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Etapa" span={2}>
                <Select
                  value={opp.stage}
                  onChange={handleStageChange}
                  style={{ width: 200 }}
                  options={Object.entries(stageLabels).map(([k, v]) => ({ value: k, label: v }))}
                />
              </Descriptions.Item>
              {opp.notes && <Descriptions.Item label="Notas" span={2}>{opp.notes}</Descriptions.Item>}
              <Descriptions.Item label="Creada">{new Date(opp.createdAt).toLocaleString('es-ES')}</Descriptions.Item>
              <Descriptions.Item label="Actualizada">{new Date(opp.updatedAt).toLocaleString('es-ES')}</Descriptions.Item>
            </Descriptions>
          </Card>

          {opp.lead && (opp.lead.budget || opp.lead.preferredZone || opp.lead.propertyType || opp.lead.bedrooms) && (
            <Card title="Preferencias del comprador" size="small" style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {opp.lead.preferredZone && <Tag color="blue">Zona: {opp.lead.preferredZone}</Tag>}
                {opp.lead.propertyType && <Tag color="purple">Tipo: {opp.lead.propertyType}</Tag>}
                {opp.lead.budget && <Tag color="gold">Presupuesto: €{opp.lead.budget.toLocaleString()}</Tag>}
                {opp.lead.bedrooms && <Tag>🛏 {opp.lead.bedrooms} hab.</Tag>}
                {opp.lead.bathrooms && <Tag>🚿 {opp.lead.bathrooms} baños</Tag>}
              </div>
            </Card>
          )}

          {opp.activities && opp.activities.length > 0 && (
            <Card title="Actividades">
              <Timeline
                items={opp.activities.map((a: any) => ({
                  color: a.type === 'NOTE' ? 'blue' : a.type === 'CALL' ? 'green' : 'gray',
                  children: (
                    <div>
                      <Text strong>{a.type}</Text> — {a.description || a.title || 'Sin descripción'}
                      <br />
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {new Date(a.createdAt).toLocaleString('es-ES')}
                      </Text>
                    </div>
                  ),
                }))}
              />
            </Card>
          )}
        </div>

        {/* Side cards */}
        <div>
          {opp.lead && (
            <Card
              title={<Space><User size={14} /> Lead</Space>}
              size="small"
              style={{ marginBottom: 16, cursor: 'pointer' }}
              onClick={() => navigate(`/leads/${opp.lead.id}`)}
              hoverable
            >
              <Text strong>{opp.lead.firstName} {opp.lead.lastName || ''}</Text><br />
              {opp.lead.email && <Text type="secondary">{opp.lead.email}</Text>}
              {opp.lead.phone && <><br /><Text type="secondary">{opp.lead.phone}</Text></>}
            </Card>
          )}

          {opp.property && (
            <Card
              title={<Space><Home size={14} /> Propiedad</Space>}
              size="small"
              style={{ cursor: 'pointer' }}
              onClick={() => navigate(`/properties/${opp.property.id}`)}
              hoverable
            >
              <Text strong>{opp.property.title}</Text><br />
              <Text type="secondary">{opp.property.address}</Text><br />
              <Text strong style={{ color: '#1F2F3F' }}>€{opp.property.price?.toLocaleString()}</Text>
            </Card>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      <Modal
        title="Editar Oportunidad"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        okText="Guardar"
        width={600}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="title" label="Título" rules={[{ required: true }]}><Input /></Form.Item>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Form.Item name="leadId" label="Lead" rules={[{ required: true }]}>
              <Select showSearch optionFilterProp="label"
                options={leads.map((l) => ({ value: l.id, label: `${l.firstName} ${l.lastName || ''} (${l.email || l.phone})` }))}
              />
            </Form.Item>
            <Form.Item name="propertyId" label="Propiedad">
              <Select allowClear showSearch optionFilterProp="label"
                options={properties.map((p) => ({ value: p.id, label: `${p.title} — €${p.price?.toLocaleString()}` }))}
              />
            </Form.Item>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Form.Item name="value" label="Valor (€)"><InputNumber style={{ width: '100%' }} min={0} /></Form.Item>
            <Form.Item name="probability" label="Probabilidad (%)"><InputNumber min={0} max={100} style={{ width: '100%' }} /></Form.Item>
          </div>
          <Form.Item name="stage" label="Etapa">
            <Select options={Object.entries(stageLabels).map(([k, v]) => ({ value: k, label: v }))} />
          </Form.Item>
          <Form.Item name="notes" label="Notas"><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </Modal>

      {/* Match Drawer */}
      <Drawer
        title={
          <Space>
            <Zap size={16} />
            <span>Match de propiedades</span>
            {matchData && (
              <Text type="secondary" style={{ fontWeight: 400, fontSize: 13 }}>
                {matchData.total.properties + matchData.total.witnesses} resultados
              </Text>
            )}
          </Space>
        }
        open={matchOpen}
        onClose={() => setMatchOpen(false)}
        width={680}
        bodyStyle={{ padding: '0 24px' }}
      >
        {matchLoading ? (
          <div style={{ textAlign: 'center', padding: 80 }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}><Text type="secondary">Buscando propiedades compatibles…</Text></div>
          </div>
        ) : matchData ? (
          <div>
            {/* Preferences summary */}
            {prefs && (prefs.zone || prefs.neighborhood || prefs.type || prefs.budget || prefs.bedrooms) && (
              <div style={{ padding: '12px 0 8px', borderBottom: '1px solid #f0f0f0', marginBottom: 4 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>Criterios del lead: </Text>
                <Space size={4} wrap>
                  {prefs.zone && <Tag color="blue">Zona: {prefs.zone}</Tag>}
                  {prefs.neighborhood && <Tag color="cyan">Barrio: {prefs.neighborhood}</Tag>}
                  {prefs.type && <Tag color="purple">{prefs.type}</Tag>}
                  {prefs.budget && <Tag color="gold">≤ €{prefs.budget.toLocaleString()}</Tag>}
                  {prefs.bedrooms && <Tag>🛏 {prefs.bedrooms}+</Tag>}
                </Space>
                {matchData?.budgetRelaxed && (
                  <div style={{ marginTop: 6 }}>
                    <Tag color="orange">⚠ Sin resultados con presupuesto — mostrando sin límite de precio</Tag>
                  </div>
                )}
              </div>
            )}

            {/* Catálogo propio */}
            <div style={{ marginTop: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <Text strong>Catálogo propio</Text>
                <Badge count={visibleProperties.length} color="#1677ff" showZero />
              </div>
              {visibleProperties.length === 0 ? (
                <Empty description="Sin resultados en el catálogo" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: '16px 0' }} />
              ) : (
                visibleProperties.map((p: any) => (
                  <MatchPropertyCard key={p.id} item={p} onLink={handleLinkProperty} onDiscard={handleDiscard} />
                ))
              )}
            </div>

            <Divider />

            {/* Testigos AVM */}
            <div style={{ marginTop: 4, marginBottom: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <Text strong>Testigos AVM</Text>
                <Space size={8}>
                  <Text type="secondary" style={{ fontSize: 12 }}>Fotocasa · Idealista</Text>
                  <Badge count={visibleWitnesses.length} color="#722ed1" showZero />
                </Space>
              </div>
              {!prefs?.zone && !prefs?.neighborhood ? (
                <Empty description="El lead no tiene zona/barrio preferido — añade uno para ver testigos" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: '16px 0' }} />
              ) : visibleWitnesses.length === 0 ? (
                <Empty description="Sin testigos en la zona indicada" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: '16px 0' }} />
              ) : (
                visibleWitnesses.map((w: any) => (
                  <MatchWitnessCard key={w.id} item={w} onDiscard={handleDiscard} />
                ))
              )}
            </div>
          </div>
        ) : null}
      </Drawer>
    </div>
  );
}
